import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DEFAULT_NOTIFICATION_PREFERENCES, type NotificationPreferences } from '@repere/core';
import { useSession } from '../auth/session';
import { useTraduction } from '../i18n/langue';
import { Carte } from '../ui';

const CLE = ['notification-preferences'] as const;

/** Les 24 heures pleines, dans l'écriture du pays de l'utilisateur. */
const HEURES = Array.from({ length: 24 }, (_, heure) => heure);

/**
 * `21:00` en France, `9 PM` en anglais — l'heure se lit comme on l'écrit chez soi.
 *
 * Ancrée en UTC : sans cela, `new Date(2000, 0, 1, 21)` serait interprétée dans le fuseau
 * du navigateur, et l'étiquette du menu ne dirait plus l'heure qu'elle représente.
 */
const libelleHeure = (heure: number, locale: string): string =>
  new Intl.DateTimeFormat(locale, {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'UTC',
  }).format(new Date(Date.UTC(2000, 0, 1, heure)));

/**
 * Les heures silencieuses, enfin réglables.
 *
 * Le produit les tient depuis le premier jour — 21h–8h, et l'API sait déjà les changer
 * comme les supprimer (`PUT /notifications/preferences`, `quietHours: null`). Rien ne
 * l'exposait : un parent qui travaille de nuit, ou qui veut simplement être prévenu à
 * l'heure où les choses arrivent, n'avait aucun moyen d'y toucher — et n'avait même pas
 * de quoi savoir pourquoi son téléphone restait muet le soir.
 *
 * **Ce que le silence n'est pas** : une suppression. Ce qui arrive pendant la plage est
 * gardé, puis remis en une seule fois à la fin — un e-mail, une notification, portant le
 * compte de ce qui s'est passé. C'est écrit sous le réglage, parce que c'est exactement ce
 * qu'on se demande avant de l'activer, et que le deviner à l'usage prend une nuit.
 *
 * L'alerte urgente, elle, franchit le silence. Elle a ses propres garde-fous (docs/02
 * §4 bis) et son propre réglage, qui n'est pas ici : couper le silence et couper l'urgence
 * sont deux gestes différents, et les réunir ferait éteindre l'un en croyant régler
 * l'autre.
 *
 * **La cadence de l'e-mail, en second.** Le silence dit *quand* on est prévenu ; celle-ci
 * dit *combien de fois*, et sur le seul canal où la question se pose. Un parent prévenu
 * sur son téléphone n'a pas besoin d'un e-mail par message, et la boîte qui en reçoit
 * trente finit par être filtrée entière — c'est-à-dire par emporter aussi celui qui
 * comptait. Le réglage est donc bien une défense, pas un confort.
 *
 * Elle est **sous** le silence et dans la même carte, parce que les deux se règlent au
 * même moment : on vient ici parce qu'on est prévenu trop souvent, et la bonne réponse est
 * tantôt l'un, tantôt l'autre. Deux cartes séparées auraient fait choisir avant d'avoir vu
 * les deux possibilités.
 */
export function ReglagesNotifications() {
  const { client } = useSession();
  const { locale, messages } = useTraduction();
  const queryClient = useQueryClient();
  const m = messages.compte;

  const { data: preferences } = useQuery({
    queryKey: CLE,
    queryFn: () => client.request<NotificationPreferences>('/notifications/preferences'),
  });

  const enregistrer = useMutation({
    mutationFn: (patch: Partial<NotificationPreferences>) =>
      client.request<NotificationPreferences>('/notifications/preferences', {
        method: 'PUT',
        body: patch,
      }),
    // La réponse porte les réglages complets tels que l'API les a retenus : on la garde
    // plutôt que de réinterroger, et l'écran affiche ce qui est enregistré, pas ce qu'on
    // a demandé.
    onSuccess: (retenus) => queryClient.setQueryData(CLE, retenus),
  });

  // Tant que la lecture n'a pas répondu, on montre les valeurs du cadrage plutôt qu'un
  // vide : elles sont celles de l'immense majorité des comptes, et un réglage qui
  // apparaît en deux temps se touche par erreur.
  const actuelles = preferences ?? DEFAULT_NOTIFICATION_PREFERENCES;
  const silence = actuelles.quietHours;
  const actif = silence !== null;

  // Le digest global l'emporte : réglé sur « une fois par jour », il retient déjà les deux
  // canaux, et montrer « un e-mail à chaque fois » décrirait alors le contraire de ce qui
  // se passe. C'est la même composition que côté API (`mailInstant`), et elle est écrite
  // aux deux endroits parce que l'écran doit pouvoir la dire sans attendre une réponse.
  const quotidien = actuelles.mailDigest === 'daily' || actuelles.digest === 'daily';

  const changerPlage = (borne: 'start' | 'end', heure: number): void => {
    const base = silence ?? DEFAULT_NOTIFICATION_PREFERENCES.quietHours;
    if (base === null) return;
    enregistrer.mutate({ quietHours: { ...base, [borne]: heure } });
  };

  return (
    <Carte titre={m.notificationsTitre}>
      <p style={{ marginTop: 0 }}>{m.silenceAide}</p>

      {/* Deux boutons plutôt qu'une bascule, comme pour la lecture des justificatifs :
          ils nomment ce qu'ils font, là où une bascule laisse deviner quel état veut dire
          quoi. `aria-pressed` dit lequel est en vigueur. */}
      <div className="choix-theme" role="group" aria-label={m.notificationsTitre}>
        <button
          type="button"
          className={actif ? 'choix-theme-option actif' : 'choix-theme-option'}
          aria-pressed={actif}
          onClick={() =>
            enregistrer.mutate({ quietHours: DEFAULT_NOTIFICATION_PREFERENCES.quietHours })
          }
          disabled={enregistrer.isPending}
        >
          {m.silenceActive}
        </button>
        <button
          type="button"
          className={!actif ? 'choix-theme-option actif' : 'choix-theme-option'}
          aria-pressed={!actif}
          // `null` et non une plage vide : c'est ce que l'API attend pour « pas de
          // silence », et une plage de zéro heure se lirait comme un réglage à corriger.
          onClick={() => enregistrer.mutate({ quietHours: null })}
          disabled={enregistrer.isPending}
        >
          {m.silenceDesactive}
        </button>
      </div>

      {/* Les bornes ne s'affichent que si le silence est actif : deux menus d'heures sans
          effet sont une invitation à croire qu'on règle quelque chose. */}
      {actif && (
        <div className="silence-plage">
          <label className="champ">
            <span>{m.silenceDebut}</span>
            <select
              value={silence.start}
              onChange={(evenement) => changerPlage('start', Number(evenement.target.value))}
              disabled={enregistrer.isPending}
            >
              {HEURES.map((heure) => (
                <option key={heure} value={heure}>
                  {libelleHeure(heure, locale)}
                </option>
              ))}
            </select>
          </label>

          <label className="champ">
            <span>{m.silenceFin}</span>
            <select
              value={silence.end}
              onChange={(evenement) => changerPlage('end', Number(evenement.target.value))}
              disabled={enregistrer.isPending}
            >
              {HEURES.map((heure) => (
                <option key={heure} value={heure}>
                  {libelleHeure(heure, locale)}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      <p className="discret">
        {actif ? m.silenceNote(libelleHeure(silence.end, locale)) : m.silenceNoteInactif}
      </p>

      <h3 className="reglage-sous-titre">{m.emailTitre}</h3>
      <p style={{ marginTop: 0 }}>{m.emailAide}</p>

      {/* Mêmes deux boutons que ci-dessus, à dessein : c'est le même geste sur un autre
          canal, et deux commandes de forme différente pour un choix de même nature
          donneraient à croire qu'ils ne s'appliquent pas de la même façon. */}
      <div className="choix-theme" role="group" aria-label={m.emailTitre}>
        <button
          type="button"
          className={!quotidien ? 'choix-theme-option actif' : 'choix-theme-option'}
          aria-pressed={!quotidien}
          onClick={() => enregistrer.mutate({ mailDigest: 'immediate' })}
          disabled={enregistrer.isPending}
        >
          {m.emailImmediat}
        </button>
        <button
          type="button"
          className={quotidien ? 'choix-theme-option actif' : 'choix-theme-option'}
          aria-pressed={quotidien}
          onClick={() => enregistrer.mutate({ mailDigest: 'daily' })}
          disabled={enregistrer.isPending}
        >
          {m.emailQuotidien}
        </button>
      </div>

      {/* L'heure d'envoi ne s'affiche que si un résumé est demandé — et elle est celle du
          digest, `digestHour`, partagée avec le regroupement global. Deux heures pour deux
          regroupements auraient obligé à en régler une qu'on ne voit jamais. */}
      {quotidien && (
        <label className="champ">
          <span>{m.emailHeure}</span>
          <select
            value={actuelles.digestHour}
            onChange={(evenement) =>
              enregistrer.mutate({ digestHour: Number(evenement.target.value) })
            }
            disabled={enregistrer.isPending}
          >
            {HEURES.map((heure) => (
              <option key={heure} value={heure}>
                {libelleHeure(heure, locale)}
              </option>
            ))}
          </select>
        </label>
      )}

      <p className="discret" style={{ marginBottom: 0 }}>
        {quotidien
          ? m.emailQuotidienNote(libelleHeure(actuelles.digestHour, locale))
          : m.emailImmediatNote}
      </p>
    </Carte>
  );
}
